import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getPoolLiquidity } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Waves } from "lucide-react";

const RANGES = ["24h", "7d", "30d"];
const MARGIN = { top: 4, right: 4, left: 4, bottom: 0 };
const X_DOMAIN = ["dataMin", "dataMax"];
const TICK_X = { fill: "#71717a", fontSize: 10 };
const TICK_FMI = { fill: "#D4AF37", fontSize: 10 };
const TICK_USDT = { fill: "#4ade80", fontSize: 10 };
const TOOLTIP_STYLE = { background: "#050505", border: "1px solid #2A2411", fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const num = (n, d = 2) => Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const fmtTime = (t, range) =>
  new Date(t * 1000).toLocaleString(undefined, range === "24h" ? { hour: "2-digit", minute: "2-digit" } : { month: "short", day: "numeric", hour: "2-digit" });
const pad = ([lo, hi]) => {
  const p = Math.max((hi - lo) * 0.2, ((hi + lo) / 2) * 0.001);
  return [lo - p, hi + p];
};

const Change = ({ value, testId }) => (
  <span data-testid={testId} className={`font-mono text-xs ${value >= 0 ? "text-green-400" : "text-red-400"}`}>{value >= 0 ? "+" : ""}{value}%</span>
);

export const LiquidityChart = () => {
  const [range, setRange] = useState("7d");
  const { data, isError } = useQuery({ queryKey: ["poolLiquidity", range], queryFn: () => getPoolLiquidity(range), refetchInterval: 8000 });

  return (
    <Card data-testid="liquidity-chart" className="border-[#2A2411] bg-[#0A0A0A] mt-6">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2"><Waves size={16} className="text-[#D4AF37]" /> Pool Liquidity</CardTitle>
        <div className="flex gap-1 rounded-full border border-[#2A2411] p-0.5">
          {RANGES.map((r) => (
            <button key={r} data-testid={`liquidity-range-${r}`} onClick={() => setRange(r)}
              className={`px-2.5 py-1 text-xs rounded-full transition-colors ${range === r ? "bg-[#D4AF37] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>
              {r.toUpperCase()}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError && <p data-testid="liquidity-error" className="text-sm text-zinc-500">Liquidity history unavailable</p>}
        {data && (
          <>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <p className="text-zinc-400">FMI <span data-testid="liquidity-fmi" className="text-[#D4AF37] font-display font-bold">{num(data.current.fmi)}</span> <Change value={data.change.fmi} testId="liquidity-fmi-change" /></p>
              <p className="text-zinc-400">USDT <span data-testid="liquidity-usdt" className="text-green-400 font-display font-bold">{num(data.current.usdt)}</span> <Change value={data.change.usdt} testId="liquidity-usdt-change" /></p>
              <p className="text-zinc-400">TVL <span data-testid="liquidity-tvl" className="text-white font-display font-bold">{num(data.tvlUsdt)} USDT</span></p>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.points} margin={MARGIN}>
                  <CartesianGrid stroke="#2A2411" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="t" type="number" domain={X_DOMAIN} tickFormatter={(t) => fmtTime(t, range)}
                    tick={TICK_X} axisLine={false} tickLine={false} minTickGap={40} />
                  <YAxis yAxisId="fmi" domain={pad} tickFormatter={(v) => num(v, 0)} tick={TICK_FMI} axisLine={false} tickLine={false} width={64} />
                  <YAxis yAxisId="usdt" orientation="right" domain={pad} tickFormatter={(v) => num(v, 0)} tick={TICK_USDT} axisLine={false} tickLine={false} width={72} />
                  <Tooltip labelFormatter={(t) => fmtTime(t, range)} contentStyle={TOOLTIP_STYLE}
                    formatter={(v, name) => [num(v, 4), name]} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Line yAxisId="fmi" type="stepAfter" dataKey="fmi" name="FMI reserve" stroke="#D4AF37" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line yAxisId="usdt" type="stepAfter" dataKey="usdt" name="USDT reserve" stroke="#4ade80" strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
